import React, { useState, useEffect } from 'react';
import { apiFetch } from './utils/api';
import PageHeader from './components/PageHeader';

function RamsTemplatesPage() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [seedingExtended, setSeedingExtended] = useState(false);
  const [seedResult, setSeedResult] = useState(null);
  const [expandedTemplate, setExpandedTemplate] = useState(null);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const res = await apiFetch('/api/rams/templates');
      if (res.ok) {
        const data = await res.json();
        setTemplates(data);
      }
    } catch (err) {
      console.error('Error fetching templates:', err);
    }
    setLoading(false);
  };

  const seedBaseTemplates = async () => {
    if (!window.confirm('Seed base RAMS templates (6 standard templates)? This will skip any that already exist.')) return;
    setSeeding(true);
    setSeedResult(null);
    try {
      const res = await apiFetch('/api/rams/templates/seed', { method: 'POST' });
      const data = await res.json();
      setSeedResult(data);
      fetchTemplates();
    } catch (err) {
      setSeedResult({ error: err.message });
    }
    setSeeding(false);
  };

  const seedExtendedTemplates = async () => {
    if (!window.confirm('Seed extended RAMS templates (12 additional: Gate/Barrier/Shutter install+repair, General, Callouts)? This will skip any that already exist.')) return;
    setSeedingExtended(true);
    setSeedResult(null);
    try {
      const res = await apiFetch('/api/rams/templates/seed-extended', { method: 'POST' });
      const data = await res.json();
      setSeedResult(data);
      fetchTemplates();
    } catch (err) {
      setSeedResult({ error: err.message });
    }
    setSeedingExtended(false);
  };

  const deleteTemplate = async (id, name) => {
    if (!window.confirm(`Delete template "${name}"? This cannot be undone.`)) return;
    try {
      const res = await apiFetch(`/api/rams/templates/${id}`, { method: 'DELETE' });
      if (res.ok) {
        fetchTemplates();
      } else {
        const err = await res.json();
        alert(err.error || 'Failed to delete');
      }
    } catch (err) {
      alert('Error: ' + err.message);
    }
  };

  const equipmentLabel = (et) => {
    const labels = {
      automatic_door: 'Automatic Door',
      manual_door: 'Manual Door',
      electric_gate: 'Electric Gate',
      roller_shutter: 'Roller Shutter',
      barrier: 'Barrier',
      fire_door: 'Fire Door',
      bigdoor: 'Big Door',
      general: 'General',
    };
    return labels[et] || et || 'Any';
  };

  const workCategoryColor = (wc) => {
    const colors = {
      installation: 'bg-blue-100 text-blue-700',
      repair: 'bg-orange-100 text-orange-700',
      service: 'bg-green-100 text-green-700',
      maintenance: 'bg-green-100 text-green-700',
      inspection: 'bg-purple-100 text-purple-700',
      callout: 'bg-red-100 text-red-700',
    };
    return colors[wc] || 'bg-gray-100 text-gray-700';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title="RAMS Templates"
        subtitle="Risk Assessment & Method Statement templates"
        backTo="/rams"
        backLabel="RAMS"
        sticky
      />

      <main className="max-w-6xl mx-auto px-6 py-8">
        {/* Seed buttons */}
        <div className="bg-white rounded-lg shadow p-6 mb-6 border border-gray-200">
          <h2 className="text-lg font-semibold text-gray-800 mb-3">Seed Templates</h2>
          <div className="flex gap-3 flex-wrap mb-3">
            <button
              onClick={seedBaseTemplates}
              disabled={seeding}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 text-sm font-semibold"
            >
              {seeding ? 'Seeding...' : 'Seed Base Templates (6)'}
            </button>
            <button
              onClick={seedExtendedTemplates}
              disabled={seedingExtended}
              className="px-4 py-2 bg-orange-600 text-white rounded hover:bg-orange-700 disabled:opacity-50 text-sm font-semibold"
            >
              {seedingExtended ? 'Seeding...' : 'Seed Extended Templates (12)'}
            </button>
          </div>
          {seedResult && (
            <div className="mt-2 text-sm">
              {seedResult.error ? (
                <span className="text-red-600">Error: {seedResult.error}</span>
              ) : (
                <span className="text-green-600">
                  ✓ {seedResult.message || `${seedResult.seeded?.length || 0} seeded, ${seedResult.skipped?.length || 0} skipped`}
                </span>
              )}
            </div>
          )}
          <p className="text-xs text-gray-500 mt-2">
            Base templates: Auto Door (Service/Repair/Install), Gate Service, Shutter Service, Barrier Service.
            Extended: Gate (Install/Repair), Barrier (Install/Repair), Shutter (Install/Repair), General (Install/Quoted/Service), Callouts (Standard/Same Day/Recall).
          </p>
        </div>

        {/* Templates list */}
        <div className="bg-white rounded-lg shadow border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-800">
              All Templates ({templates.length})
            </h2>
          </div>

          {loading ? (
            <div className="p-8 text-center text-gray-500">Loading templates...</div>
          ) : templates.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              No templates found. Use the seed buttons above to add templates.
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {templates.map((t) => (
                <div key={t.id} className="px-6 py-4 hover:bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 flex-1">
                      <div
                        className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-bold"
                      >
                        {t.equipment_type ? equipmentLabel(t.equipment_type).slice(0, 2).toUpperCase() : 'GE'}
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-800 text-sm">{t.name}</h3>
                        <div className="flex gap-2 mt-1">
                          <span className="text-xs text-gray-500">{equipmentLabel(t.equipment_type)}</span>
                          {t.work_category && (
                            <span className={`text-xs px-2 py-0.5 rounded-full ${workCategoryColor(t.work_category)}`}>
                              {t.work_category}
                            </span>
                          )}
                          {t.organization_id === null && (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">system</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setExpandedTemplate(expandedTemplate === t.id ? null : t.id)}
                        className="text-xs text-blue-600 hover:text-blue-800"
                      >
                        {expandedTemplate === t.id ? 'Hide' : 'Preview'}
                      </button>
                      <button
                        onClick={() => deleteTemplate(t.id, t.name)}
                        className="text-xs text-red-600 hover:text-red-800"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                  {expandedTemplate === t.id && (
                    <TemplatePreview templateId={t.id} />
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

// Inline preview component — fetches full template data
function TemplatePreview({ templateId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTemplate();
  }, [templateId]);

  const fetchTemplate = async () => {
    setLoading(true);
    try {
      const res = await apiFetch(`/api/rams/templates/${templateId}`);
      if (res.ok) {
        const d = await res.json();
        setData(d);
      }
    } catch (err) {
      console.error('Error fetching template:', err);
    }
    setLoading(false);
  };

  if (loading) return <div className="mt-3 text-sm text-gray-500">Loading preview...</div>;
  if (!data || !data.template_data) return <div className="mt-3 text-sm text-gray-500">No data available</div>;

  const td = typeof data.template_data === 'string' ? JSON.parse(data.template_data) : data.template_data;

  return (
    <div className="mt-3 ml-13 pl-4 border-l-2 border-blue-200 text-sm">
      {td.scope_items && (
        <div className="mb-2">
          <strong className="text-gray-700">Scope ({td.scope_items.length} items):</strong>
          <ul className="text-xs text-gray-600 ml-4 mt-1">
            {td.scope_items.slice(0, 3).map((s, i) => <li key={i}>• {s.task}</li>)}
            {td.scope_items.length > 3 && <li className="text-gray-400">... +{td.scope_items.length - 3} more</li>}
          </ul>
        </div>
      )}
      {td.risks && (
        <div className="mb-2">
          <strong className="text-gray-700">Risks ({td.risks.length}):</strong>
          <div className="flex gap-1 flex-wrap ml-4 mt-1">
            {td.risks.map((r, i) => (
              <span key={i} className={`text-xs px-2 py-0.5 rounded ${
                r.initial_risk === 'HIGH' ? 'bg-red-100 text-red-700' :
                r.initial_risk === 'MED' ? 'bg-orange-100 text-orange-700' :
                'bg-green-100 text-green-700'
              }`}>{r.ref}</span>
            ))}
          </div>
        </div>
      )}
      {td.method_steps && (
        <div className="mb-2">
          <strong className="text-gray-700">Method Steps ({td.method_steps.length}):</strong>
          <div className="text-xs text-gray-600 ml-4 mt-1">
            {[...new Set(td.method_steps.map(s => s.section))].join(' | ')}
          </div>
        </div>
      )}
      {td.ppe && (
        <div className="mb-2">
          <strong className="text-gray-700">PPE ({td.ppe.length} items):</strong>
          <div className="text-xs text-gray-600 ml-4 mt-1">
            {td.ppe.filter(p => p.required).map(p => p.item).join(', ')}
          </div>
        </div>
      )}
    </div>
  );
}

export default RamsTemplatesPage;
